import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import config from "../../config";
import Sidebar from "../Sidebar/Sidebar";

const drawerWidth = 270;
const FILTER_TYPE = "buyer";

const FILTER_THEME = {
  color: "#DB2777",
  bg: "rgba(219,39,119,0.10)",
  icon: ShoppingCartRoundedIcon,
};

const numberFormat = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString("en-IN");
};

const dateFormat = (value) => {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const dateTimeFormat = (value) => {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getToday = () => dayjs().format("YYYY-MM-DD");

const getReportTotals = (reports = []) => {
  return reports.reduce(
    (total, report) => {
      total.quantity += Number(report?.totalQuantity || 0);
      total.purchase += Number(report?.totalPurchase || 0);

      const buyerName = String(report?.buyer?.name || "").trim();
      if (buyerName) {
        total.buyers.add(buyerName.toLowerCase());
      }

      total.reports += 1;

      return total;
    },
    {
      quantity: 0,
      purchase: 0,
      buyers: new Set(),
      reports: 0,
    }
  );
};

const getVarietyNames = (report) => {
  const varieties = Array.isArray(report?.varieties)
    ? report.varieties
    : [];

  const names = varieties
    .map((item) => String(item?.variety || "").trim())
    .filter(Boolean);

  return names.length ? names : ["—"];
};

const getSearchableText = (report) => {
  const varietyNames = (report?.varieties || [])
    .map((item) => item?.variety)
    .filter(Boolean);

  return [
    report?.buyer?.name,
    report?.mandi?.name,
    report?.mandi?.mandiCode,
    report?.mandi?.location,
    report?.mandi?.businessName,
    report?.mandi?.state,
    report?.reportSource,
    ...varietyNames,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const ReportByBuyerWise = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlDateType = searchParams.get("dateType");
  const urlDate = searchParams.get("date");

  const initialIsLifetime = urlDateType === "lifetime";
  const initialDate = urlDate || getToday();

  const [isLifetime, setIsLifetime] = useState(initialIsLifetime);
  const [date, setDate] = useState(initialDate);
  const [pendingLifetime, setPendingLifetime] = useState(initialIsLifetime);
  const [pendingDate, setPendingDate] = useState(initialDate);

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("");
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = sessionStorage.getItem("token") || "";
      const accessToken =
        sessionStorage.getItem("x-access-token") || token || "";
      const userId = sessionStorage.getItem("user-id") || "";

      const params = new URLSearchParams({
        filterType: FILTER_TYPE,
        dateType: isLifetime ? "lifetime" : "date",
      });

      if (!isLifetime) {
        params.set("date", date);
      }

      const response = await fetch(
        `${config.apiBaseUrl}/campaign-call/report-filter-type?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-access-token": accessToken,
            "user-id": userId,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}`
        );
      }

      const json = await response.json();

      if (json?.success === false) {
        throw new Error(
          json?.message || "Unable to load buyer reports."
        );
      }

      const groups = Array.isArray(json?.data) ? json.data : [];

      setData(groups);
      setMeta(json);

      // Keep the UI synchronized with the API-selected filter.
      if (json?.selected?.dateType === "date" && json?.selected?.date) {
        setIsLifetime(false);
        setPendingLifetime(false);
        setDate(json.selected.date);
        setPendingDate(json.selected.date);
      } else if (json?.selected?.dateType === "lifetime") {
        setIsLifetime(true);
        setPendingLifetime(true);
      }

      setActiveTab((current) => {
        const exists = groups.some(
          (group) => group?.tabName === current
        );

        return exists
          ? current
          : groups[0]?.tabName || "";
      });
    } catch (err) {
      setError(
        err?.message || "Unable to load buyer reports."
      );
      setData([]);
      setMeta(null);
      setActiveTab("");
    } finally {
      setLoading(false);
    }
  }, [date, isLifetime]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleApplyDateFilter = () => {
    setIsLifetime(pendingLifetime);
    setDate(pendingDate);

    if (pendingLifetime) {
      setSearchParams({
        dateType: "lifetime",
      });
    } else {
      setSearchParams({
        dateType: "date",
        date: pendingDate,
      });
    }
  };

  const handleDateRangeToggle = (_event, value) => {
    if (value === null) return;

    setPendingLifetime(value === "lifetime");
  };

  const handleClearFilter = () => {
    const today = getToday();

    setPendingLifetime(false);
    setPendingDate(today);
    setIsLifetime(false);
    setDate(today);
    setSearch("");

    setSearchParams({
      dateType: "date",
      date: today,
    });
  };

  const hasPendingChange =
    pendingLifetime !== isLifetime ||
    (!pendingLifetime && pendingDate !== date);

  const hasAppliedFilter =
    isLifetime ||
    date !== getToday() ||
    Boolean(search);

  const handleBack = () => {
    navigate("/ivr-anaylitics");
  };

  const visibleTabs = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return data;

    return data.filter((group) => {
      if (
        String(group?.tabName || "")
          .toLowerCase()
          .includes(term)
      ) {
        return true;
      }

      return (group?.reports || []).some((report) =>
        getSearchableText(report).includes(term)
      );
    });
  }, [data, search]);

  useEffect(() => {
    if (!visibleTabs.length) {
      setActiveTab("");
      return;
    }

    const stillExists = visibleTabs.some(
      (group) => group?.tabName === activeTab
    );

    if (!stillExists) {
      setActiveTab(visibleTabs[0]?.tabName || "");
    }
  }, [visibleTabs, activeTab]);

  const activeGroup = useMemo(() => {
    return (
      visibleTabs.find(
        (group) => group?.tabName === activeTab
      ) || visibleTabs[0]
    );
  }, [visibleTabs, activeTab]);

  const activeReports = useMemo(
    () => activeGroup?.reports || [],
    [activeGroup]
  );

  const totals = useMemo(
    () => getReportTotals(activeReports),
    [activeReports]
  );

  const buyerCount = totals.buyers.size;

  const summaryCards = [
    {
      label: "Total Groups",
      value: meta?.totalGroups || 0,
      sub: "Buyer groups",
      icon: DescriptionRoundedIcon,
      color: "#2563EB",
      bg: "rgba(37,99,235,0.10)",
    },
    {
      label: "Total Reports",
      value: meta?.totalReports || 0,
      sub: "Submitted reports",
      icon: DescriptionRoundedIcon,
      color: "#0891B2",
      bg: "rgba(8,145,178,0.10)",
    },
    {
      label: "Total Quantity",
      value: totals.quantity,
      sub: activeGroup?.tabName || "Active buyer",
      icon: Inventory2RoundedIcon,
      color: "#16A34A",
      bg: "rgba(22,163,74,0.10)",
    },
    {
      label: "Total Purchase",
      value: totals.purchase,
      sub: `${numberFormat(buyerCount)} buyer(s)`,
      icon: ShoppingCartRoundedIcon,
      color: "#D97706",
      bg: "rgba(217,119,6,0.10)",
    },
  ];

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(15,23,42,0.06)",
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
    overflow: "hidden",
  };

  const tableHeadSx = {
    fontWeight: 800,
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#475569",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    whiteSpace: "nowrap",
  };

  const tableCellSx = {
    fontSize: "0.80rem",
    borderBottom: "1px solid rgba(15,23,42,0.07)",
    whiteSpace: "nowrap",
    verticalAlign: "top",
  };

  const toggleGroupSx = {
    background: "#fff",
    borderRadius: "10px",
    p: 0.4,
    border: "1px solid rgba(15,23,42,0.08)",
    "& .MuiToggleButton-root": {
      border: "none",
      borderRadius: "8px !important",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.78rem",
      color: "rgba(15,23,42,0.55)",
      px: 1.2,
      gap: 0.5,
    },
    "& .Mui-selected": {
      background:
        "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%) !important",
      color: "#fff !important",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: "#F8FAFC",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            md: `calc(100% - ${drawerWidth}px)`,
          },
          p: {
            xs: 1.5,
            sm: 2.5,
            md: 4,
          },
          overflowX: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          rowGap={1.5}
          mb={{ xs: 2, md: 3 }}
        >
          {isMobile && (
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "12px",
              }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}

          <IconButton
            onClick={handleBack}
            sx={{
              border: "1px solid rgba(15,23,42,0.1)",
              borderRadius: "12px",
              background: "#fff",
              flexShrink: 0,
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>

          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: FILTER_THEME.color,
              background: FILTER_THEME.bg,
            }}
          >
            <FILTER_THEME.icon sx={{ fontSize: 27 }} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              rowGap={0.5}
            >
              <Typography
                variant={isMobile ? "h6" : "h4"}
                fontWeight={800}
                noWrap={!isMobile}
              >
                Buyer Wise Reports
              </Typography>

              <Chip
                size="small"
                label="Buyer"
                sx={{
                  fontWeight: 700,
                  color: FILTER_THEME.color,
                  background: FILTER_THEME.bg,
                }}
              />
            </Stack>

            <Typography
              sx={{
                color: "rgba(15,23,42,0.55)",
                fontSize: "0.85rem",
              }}
            >
              Buyer-wise mandi reports
              {" · "}
              {isLifetime ? "Lifetime" : dateFormat(date)}
            </Typography>
          </Box>

          <IconButton
            onClick={fetchReports}
            disabled={loading}
            sx={{
              border: "1px solid rgba(15,23,42,0.1)",
              borderRadius: "12px",
              background: "#fff",
              flexShrink: 0,
            }}
          >
            <RefreshRoundedIcon
              sx={{
                animation: loading
                  ? "spin 1s linear infinite"
                  : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Stack>

        <Card
          elevation={0}
          sx={{
            ...sectionCardSx,
            p: { xs: 1.25, sm: 1.5 },
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Chip
              icon={
                <FILTER_THEME.icon
                  sx={{
                    fontSize: "16px !important",
                    color: `${FILTER_THEME.color} !important`,
                  }}
                />
              }
              label="Buyer"
              sx={{
                height: 38,
                fontWeight: 700,
                fontSize: "0.8rem",
                borderRadius: "10px",
                background: FILTER_THEME.bg,
                color: FILTER_THEME.color,
                flexShrink: 0,
              }}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", sm: "block" },
                my: 0.25,
              }}
            />

            <ToggleButtonGroup
              value={pendingLifetime ? "lifetime" : "date"}
              exclusive
              size="small"
              onChange={handleDateRangeToggle}
              sx={{ ...toggleGroupSx, flexShrink: 0 }}
            >
              <ToggleButton value="date">
                <TodayRoundedIcon sx={{ fontSize: 16 }} />
                By Date
              </ToggleButton>

              <ToggleButton value="lifetime">
                <AllInclusiveRoundedIcon sx={{ fontSize: 16 }} />
                Lifetime
              </ToggleButton>
            </ToggleButtonGroup>

            {!pendingLifetime && (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
              >
                <DatePicker
                  value={
                    pendingDate ? dayjs(pendingDate) : null
                  }
                  onChange={(value) => {
                    if (!value) return;
                    setPendingDate(
                      value.format("YYYY-MM-DD")
                    );
                  }}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                  sx={{
                    width: { xs: "100%", sm: 145 },
                    flexShrink: 0,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </LocalizationProvider>
            )}

            <Button
              variant="outlined"
              size="small"
              disabled={
                !hasAppliedFilter && !hasPendingChange
              }
              onClick={handleClearFilter}
              startIcon={
                <ClearRoundedIcon sx={{ fontSize: 15 }} />
              }
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: "rgba(220,38,38,0.3)",
                color: "#B91C1C",
                flexShrink: 0,
                "&:hover": {
                  borderColor: "#DC2626",
                  background: "rgba(220,38,38,0.04)",
                },
              }}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={handleApplyDateFilter}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                background:
                  "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                flexShrink: 0,
              }}
            >
              Apply
            </Button>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", sm: "block" },
                my: 0.25,
              }}
            />

            <TextField
              size="small"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search buyer, mandi, state, variety..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      fontSize="small"
                      sx={{
                        color: "rgba(15,23,42,0.4)",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 260px",
                },
                minWidth: {
                  xs: "100%",
                  sm: 220,
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Box>
        </Card>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: "12px" }}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {loading
            ? Array.from({ length: summaryCards.length }).map(
                (_, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <Skeleton
                      variant="rounded"
                      height={105}
                      sx={{ borderRadius: "16px" }}
                    />
                  </Grid>
                )
              )
            : summaryCards.map((card) => {
                const Icon = card.icon;

                return (
                  <Grid
                    item
                    xs={6}
                    sm={3}
                    key={card.label}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        ...sectionCardSx,
                        p: { xs: 1.5, md: 2 },
                        height: "100%",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            width: {
                              xs: 42,
                              md: 52,
                            },
                            height: {
                              xs: 42,
                              md: 52,
                            },
                            flexShrink: 0,
                            borderRadius: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: card.bg,
                            color: card.color,
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: {
                                xs: 21,
                                md: 26,
                              },
                            }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              color:
                                "rgba(15,23,42,0.55)",
                              fontWeight: 600,
                              fontSize: "0.76rem",
                            }}
                          >
                            {card.label}
                          </Typography>

                          <Typography
                            noWrap
                            sx={{
                              fontSize: {
                                xs: "1.15rem",
                                md: "1.45rem",
                              },
                              fontWeight: 800,
                            }}
                          >
                            {numberFormat(card.value)}
                          </Typography>

                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              color: card.color,
                            }}
                          >
                            {card.sub}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
        </Grid>

        <Card elevation={0} sx={sectionCardSx}>
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              p: 1.5,
              overflowX: "auto",
              borderBottom:
                "1px solid rgba(15,23,42,0.07)",
              background: "#fff",
              "&::-webkit-scrollbar": {
                height: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  "rgba(15,23,42,0.15)",
                borderRadius: 3,
              },
            }}
          >
            {loading && !visibleTabs.length
              ? Array.from({ length: 4 }).map(
                  (_, index) => (
                    <Skeleton
                      key={index}
                      variant="rounded"
                      width={100}
                      height={30}
                      sx={{
                        borderRadius: "16px",
                        flexShrink: 0,
                      }}
                    />
                  )
                )
              : visibleTabs.map((group) => {
                  const tabName =
                    group?.tabName || "Unknown";
                  const isActive =
                    activeGroup?.tabName === tabName;

                  return (
                    <Chip
                      key={tabName}
                      label={`${tabName} (${
                        group?.reports?.length || 0
                      })`}
                      onClick={() =>
                        setActiveTab(tabName)
                      }
                      sx={{
                        flexShrink: 0,
                        fontWeight: 700,
                        borderRadius: "10px",
                        border: isActive
                          ? "none"
                          : "1px solid rgba(15,23,42,0.12)",
                        background: isActive
                          ? "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)"
                          : "#fff",
                        color: isActive
                          ? "#fff"
                          : "rgba(15,23,42,0.7)",
                        "&:hover": {
                          background: isActive
                            ? "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)"
                            : "#F8FAFC",
                        },
                      }}
                    />
                  );
                })}

            {!loading && visibleTabs.length === 0 && (
              <Typography
                fontSize="0.85rem"
                color="text.secondary"
                py={0.5}
                px={1}
              >
                No buyers found
              </Typography>
            )}
          </Box>

          {loading ? (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Skeleton variant="text" width={180} height={32} />
              <Skeleton
                variant="text"
                width={100}
                height={20}
                sx={{ mb: 2 }}
              />
              <Skeleton
                variant="rounded"
                height={280}
                sx={{ borderRadius: "12px" }}
              />
            </Box>
          ) : !activeGroup ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{
                py: 8,
                color: "rgba(15,23,42,0.4)",
              }}
            >
              <InboxRoundedIcon
                sx={{ fontSize: 46, opacity: 0.4 }}
              />

              <Typography
                fontWeight={700}
                color="text.secondary"
              >
                No buyer reports found
              </Typography>

              <Typography
                fontSize="0.8rem"
                color="text.secondary"
              >
                Try another date or clear the search.
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ p: { xs: 1.25, md: 2 } }}>
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                spacing={1}
                mb={2}
              >
                <Box>
                  <Typography
                    fontWeight={800}
                    fontSize="1.1rem"
                  >
                    {activeGroup.tabName}
                  </Typography>

                  <Typography
                    fontSize="0.8rem"
                    color="text.secondary"
                  >
                    {activeReports.length} report(s)
                  </Typography>
                </Box>

                <Chip
                  icon={
                    <PeopleRoundedIcon
                      sx={{ fontSize: 16 }}
                    />
                  }
                  label={`${numberFormat(
                    buyerCount
                  )} buyer(s)`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    background:
                      "rgba(219,39,119,0.10)",
                    color: "#DB2777",
                  }}
                />
              </Stack>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor:
                    "rgba(15,23,42,0.08)",
                  maxHeight: {
                    xs: 520,
                    md: 620,
                  },
                  overflowX: "auto",
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{ minWidth: 1320 }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        "Date",
                        "Mandi Partner",
                        "Location",
                        "Varieties",
                        "Total Quantity",
                        "Purchase",
                      ].map((heading) => (
                        <TableCell
                          key={heading}
                          sx={tableHeadSx}
                          align={
                            ["Total Quantity", "Purchase"].includes(heading)
                              ? "right"
                              : "left"
                          }
                        >
                          {heading}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {activeReports.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{
                            py: 6,
                            color: "text.secondary",
                          }}
                        >
                          No reports found for{" "}
                          {activeGroup.tabName}.
                        </TableCell>
                      </TableRow>
                    ) : (
                      activeReports.map(
                        (report, index) => {
                          const rowId =
                            report?._id ||
                            `${activeGroup.tabName}-${index}`;

                                                    return (
                            <TableRow
                              hover
                              key={rowId}
                            >
                              <TableCell sx={tableCellSx}>
                                <Stack spacing={0.25}>
                                  <Typography
                                    fontWeight={700}
                                    fontSize="0.78rem"
                                  >
                                    {dateFormat(
                                      report?.callDate
                                    )}
                                  </Typography>

                                  <Typography
                                    fontSize="0.66rem"
                                    color="text.secondary"
                                  >
                                    {dateTimeFormat(
                                      report?.createdAt
                                    )}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              <TableCell
                                sx={{
                                  ...tableCellSx,
                                  whiteSpace: "normal",
                                  minWidth: 190,
                                }}
                              >
                                <Typography
                                  fontWeight={700}
                                  fontSize="0.8rem"
                                >
                                  {report?.mandi?.name || "—"}
                                </Typography>

                                <Typography
                                  fontSize="0.7rem"
                                  color="text.secondary"
                                  sx={{
                                    maxWidth: 260,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {report?.mandi?.businessName || "—"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {report?.mandi?.location || "—"}
                              </TableCell>

                              <TableCell
                                sx={{
                                  ...tableCellSx,
                                  whiteSpace: "normal",
                                  minWidth: 180,
                                }}
                              >
                                <Stack spacing={0.5}>
                                  {getVarietyNames(report).map((name, varietyIndex) => (
                                    <Typography
                                      key={`${rowId}-variety-${varietyIndex}`}
                                      fontSize="0.76rem"
                                      fontWeight={700}
                                      color={name === "—" ? "text.secondary" : "#B45309"}
                                    >
                                      {name}
                                    </Typography>
                                  ))}
                                </Stack>
                              </TableCell>

                              <TableCell
                                sx={{
                                  ...tableCellSx,
                                  fontWeight: 800,
                                }}
                                align="right"
                              >
                                {numberFormat(
                                  report?.totalQuantity
                                )}
                              </TableCell>

                              <TableCell
                                sx={{
                                  ...tableCellSx,
                                  fontWeight: 800,
                                  color: "#B45309",
                                }}
                                align="right"
                              >
                                {numberFormat(report?.totalPurchase)}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Card>

        <Typography
          sx={{
            textAlign: "center",
            color: "rgba(15,23,42,0.4)",
            fontSize: "0.72rem",
            py: 2,
          }}
        >
          Buyer-wise reports · Data based on submitted mandi
          reports
        </Typography>
      </Box>
    </Box>
  );
};

export default ReportByBuyerWise;
